import { AnalyticsModuleBootstrapError, logger } from "./debug";
import { getRegion } from "./generalUtils/envFns";
import {
  deleteCookie,
  getActiveGroupIds,
  getCookiesForGroup,
  getDomainCandidates,
  getGroupId,
  getPathCandidates,
  getVisibleCookieNames,
  isHostFirstParty,
} from "./generalUtils/oneTrustUtils";
import type {
  OneTrustCookieDefinition,
  OneTrustWindow,
} from "./generalUtils/oneTrustUtils";

/**
 * Deletes OneTrust category cookies for categories that are currently opted out.
 */
function clearOptedOutCategoryCookiesAttempt(
  oneTrustWindow: OneTrustWindow
): void {
  try {
    const domainData = oneTrustWindow.OneTrust?.GetDomainData?.();
    const groups = domainData?.Groups;

    if (!groups || groups.length === 0) {
      return;
    }

    const activeGroupIds = getActiveGroupIds(oneTrustWindow);
    if (activeGroupIds.size === 0) {
      return;
    }

    // Single pass: partition cookies into accepted (active groups) vs rejected
    const acceptedCookies = new Set<string>();
    const rejectedCookies: OneTrustCookieDefinition[] = [];

    for (const group of groups) {
      const groupId = getGroupId(group);
      if (!groupId) continue;

      const groupCookies = getCookiesForGroup(group);
      if (activeGroupIds.has(groupId)) {
        for (const cookie of groupCookies) {
          const name = cookie.Name?.trim();
          if (name) acceptedCookies.add(name);
        }
      } else {
        rejectedCookies.push(...groupCookies);
      }
    }

    const visibleCookiesBeforeDeletion = getVisibleCookieNames();
    const attemptedDeletedCookies = new Set<string>();
    const skippedThirdPartyCookies = new Set<string>();

    // Cache domain/path candidates — same for all cookies on this page
    const domainCandidates = getDomainCandidates(window.location.hostname);
    const pathCandidates = getPathCandidates(window.location.pathname || "/");

    console.log("OneTrust category cookie cleanup: Starting deletion process", {
      domainCandidates,
      pathCandidates,
    });

    for (const cookie of rejectedCookies) {
      const normalizedCookieName = cookie.Name?.trim();
      if (!normalizedCookieName) continue;
      if (acceptedCookies.has(normalizedCookieName)) continue;

      if (cookie.Host && !isHostFirstParty(cookie.Host)) {
        skippedThirdPartyCookies.add(normalizedCookieName);
        continue;
      }

      deleteCookie(
        normalizedCookieName,
        domainCandidates,
        pathCandidates,
        cookie.Host
      );
      attemptedDeletedCookies.add(normalizedCookieName);
    }

    const visibleCookiesAfterDeletion = getVisibleCookieNames();
    const deletedVisibleCookies: string[] = [];
    const stillVisibleAfterDeletion: string[] = [];

    for (const cookieName of attemptedDeletedCookies) {
      if (!visibleCookiesBeforeDeletion.has(cookieName)) continue;
      if (visibleCookiesAfterDeletion.has(cookieName)) {
        stillVisibleAfterDeletion.push(cookieName);
      } else {
        deletedVisibleCookies.push(cookieName);
      }
    }
  } catch (error) {
    logger.error(
      new AnalyticsModuleBootstrapError(
        "Failed to clear opted-out category cookies",
        { cause: error }
      )
    );
  }
}

const COOKIE_CLEANUP_REGIONS = new Set(["mx"]);

export function clearOptedOutCategoryCookies(): void {
  const oneTrustWindow = window as OneTrustWindow;
  if (COOKIE_CLEANUP_REGIONS.has(getRegion())) {
    clearOptedOutCategoryCookiesAttempt(oneTrustWindow);
  }
}

function appendMxAutoBlockScript(
  oneTrustId: string,
  reject: (reason?: Error) => void
): void {
  const oneTrustAutoBlockScript = document.createElement("script");
  oneTrustAutoBlockScript.id = "lll-onetrust-autoblock-script";
  oneTrustAutoBlockScript.src = `https://cdn.cookielaw.org/consent/${oneTrustId}/OtAutoBlock.js`;
  oneTrustAutoBlockScript.type = "text/javascript";
  oneTrustAutoBlockScript.async = true;

  oneTrustAutoBlockScript.onerror = (event) => {
    reject(
      new Error(
        `Failed to load OneTrust AutoBlock script for ID: ${oneTrustId}`,
        { cause: event }
      )
    );
  };

  document.head.appendChild(oneTrustAutoBlockScript);
}

/**
 * Loads the OneTrust script
 *
 * @param oneTrustId - The OneTrust domain script ID (required).
 * @param dataLayerName - Optional name of the data layer OneTrust should target. Defaults to OneTrust's built-in "dataLayer".
 * @returns Promise<void>
 */
export function loadOneTrustScript(
  oneTrustId?: string,
  dataLayerName: string = ""
): Promise<void> {
  if (!oneTrustId)
    return Promise.reject(new Error("OneTrust domain script ID is required."));

  return new Promise<void>((resolve, reject) => {
    const oneTrustWindow = window as OneTrustWindow;
    if (typeof oneTrustWindow.OptanonWrapper !== "function") {
      oneTrustWindow.OptanonWrapper = () => {};
    }

    const oneTrustScript = document.createElement("script");
    oneTrustScript.id = "lll-onetrust-script";
    oneTrustScript.src =
      "https://cdn.cookielaw.org/scripttemplates/otSDKStub.js";
    oneTrustScript.setAttribute("data-document-language", "true");
    oneTrustScript.type = "text/javascript";
    oneTrustScript.charset = "UTF-8";
    oneTrustScript.setAttribute("data-domain-script", oneTrustId);
    oneTrustScript.async = true;

    if (dataLayerName) {
      // OneTrust uses "dataLayer" by default as the data layer name
      oneTrustScript.setAttribute("data-dLayer-name", dataLayerName);
    }

    oneTrustScript.onload = () => {
      // Script loaded successfully
      resolve();
    };

    oneTrustScript.onerror = (event) => {
      // Script failed to load
      reject(
        new Error(`Failed to load OneTrust script for ID: ${oneTrustId}`, {
          cause: event,
        })
      );
    };

    document.head.appendChild(oneTrustScript);
  });
}
